import type { ServerState, SoftwareFamily } from './api/types';

export const softwareLabel: Record<SoftwareFamily, string> = {
  vanilla: 'Vanilla',
  paper: 'Paper',
  purpur: 'Purpur',
  fabric: 'Fabric',
  neoforge: 'NeoForge',
  forge: 'Forge',
  youer: 'Youer',
  asyncyouer: 'AsyncYouer',
  mohist: 'Mohist',
};

export const stateInfo: Record<
  ServerState,
  { label: string; tone: 'ok' | 'warn' | 'danger' | 'info' | 'muted'; busy?: boolean }
> = {
  running: { label: '稼働中', tone: 'ok' },
  stopped: { label: '停止中', tone: 'muted' },
  starting: { label: '起動中', tone: 'warn', busy: true },
  stopping: { label: '停止処理中', tone: 'warn', busy: true },
  provisioning: { label: '作成中', tone: 'warn', busy: true },
  deleting: { label: '削除中', tone: 'warn', busy: true },
  error: { label: 'エラー', tone: 'danger' },
};

export const jobKindLabel: Record<string, string> = {
  snapshot: 'スナップショット',
  change_software: 'ソフト変更',
  change_version: 'バージョン変更',
  restore_snapshot: '復元',
  delete_server: '削除',
  change_mods: 'Mod・プラグインの変更',
  change_modpack: 'Modpack の導入・更新',
  change_bedrock: '統合版の設定変更',
  change_ports: '追加ポートの変更',
  change_game_settings: 'ゲーム設定の変更',
  change_world_generation: '次回のワールド生成設定の保存',
  regenerate_world: 'ワールドの再生成',
  change_jvm_args: '起動引数の変更',
  change_resources: 'リソースの変更',
  move_node: 'ノードの移動',
  change_world_ip: '固定 IP への移行',
  apply_edge: '接続先の反映',
  create: '作成',
  start: '起動',
  stop: '停止',
  restart: '再起動',
  client_pack_test: 'クライアント Mod の起動テスト',
};

export const stepLabel: Record<string, string> = {
  preflight: '起動テストの準備',
  'wait-vm': 'テスト用 VM の空き待ち',
  'launch-test': 'テスト用 VM で起動テスト',
  'save-generation': '次回の生成設定を保存',
  'pre-regenerate-snapshot': '再生成前のスナップショット',
  'prune-scheduled-snapshots': '古い定期スナップショットを削除',
  snapshot: 'スナップショットを取る',
  'trash-worlds-and-write-generation': 'ワールドを退避して生成設定を書き込み',
  'change-jvm-and-start': '起動引数を変更して起動（失敗時は巻き戻し）',
  'write-port-config': 'ポート設定を書き換え',
  'save-ports': 'ポート設定を保存',
  'apply-ports-edge': 'edge に転送を反映',
  'select-node': 'ノード選定',
  'next-vmid': 'VMID の取得',
  clone: 'VM の複製',
  configure: 'VM の設定',
  'vm-start': 'VM の起動',
  'agent-config': 'agent の接続設定',
  'agent-update': 'agent の版を確認・更新',
  prepare: 'サーバーの準備',
  'cache-bedrock': '統合版の取得・検証',
  'install-bedrock': '統合版の配置・設定',
  'apply-bedrock-edge': 'UDP 転送の反映',
  'agent-prepare': '設定の反映',
  'agent-start': 'Minecraft の起動',
  'set-cpu-memory': 'CPU・メモリの変更',
  'resize-vm-disk': 'ディスクの拡大',
  'grow-filesystem': 'ファイルシステムの拡大',
  'save-resources': '設定の保存',
  'pre-change-snapshot': '変更前のスナップショット',
  'agent-stop': 'Minecraft の停止',
  'vm-shutdown': 'VM の停止',
};

export const jobStatusInfo: Record<string, { label: string; tone: 'ok' | 'warn' | 'danger' | 'info' | 'muted' }> = {
  queued: { label: '待機', tone: 'muted' },
  running: { label: '実行中', tone: 'warn' },
  succeeded: { label: '成功', tone: 'ok' },
  failed: { label: '失敗', tone: 'danger' },
  cancelled: { label: '中止', tone: 'muted' },
};

export const viaLabel: Record<string, string> = {
  web: 'Web',
  mcp: 'AI (MCP)',
  system: 'システム',
  agent: 'agent',
};

export const roleLabel: Record<string, string> = {
  owner: 'オーナー',
  admin: '管理者',
  viewer: '閲覧のみ',
};

export function formatGb(mb: number): string {
  const gb = mb / 1024;
  return gb >= 10 ? `${gb.toFixed(0)}GB` : `${gb.toFixed(1)}GB`;
}

export function percent(ratio: number | undefined): string {
  if (ratio === undefined || Number.isNaN(ratio)) return '—';
  return `${Math.round(ratio * 100)}%`;
}

export function formatDateTime(iso: string | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return date.toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function formatActivityTime(iso: string, now = new Date()): string {
  const date = new Date(iso);
  const today = date.toDateString() === now.toDateString();
  return date.toLocaleString('ja-JP', {
    ...(today ? {} : { month: 'numeric' as const, day: 'numeric' as const }),
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatRelative(iso: string | undefined): string {
  if (!iso) return '—';
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return `${Math.max(seconds, 0)}秒前`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}分前`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}時間前`;
  return `${Math.floor(seconds / 86400)}日前`;
}

export function formatDuration(startIso: string | undefined, endIso: string | undefined): string {
  if (!startIso) return '—';
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  const seconds = Math.max(0, Math.round((end - new Date(startIso).getTime()) / 1000));
  if (seconds < 60) return `${seconds}秒`;
  return `${Math.floor(seconds / 60)}分${seconds % 60}秒`;
}

export function tpsTone(tps: number | undefined): 'ok' | 'warn' | 'danger' | 'muted' {
  if (tps === undefined) return 'muted';
  if (tps >= 19) return 'ok';
  if (tps >= 15) return 'warn';
  return 'danger';
}

export const requestKindLabel: Record<string, string> = {
  create_server: 'サーバー作成',
  delete_server: 'サーバー削除',
  change_software: 'ソフト変更',
  change_version: 'バージョン変更',
  restore_snapshot: 'スナップショットから復元',
  delete_snapshot: 'スナップショット削除',
  console_command: 'コンソールコマンド',
  change_mods: 'Mod・プラグインの変更',
  change_modpack: 'Modpack の導入・更新',
  change_bedrock: '統合版の設定変更',
  change_ports: '追加ポートの変更',
  change_game_settings: 'ゲーム設定の変更',
  change_jvm_args: '起動引数の変更',
  change_resources: 'リソースの変更',
  move_node: 'ノードの移動',
  change_world_ip: '固定 IP への移行',
  apply_edge: '接続先の反映',
};

export const requestStatusInfo: Record<string, { label: string; tone: 'ok' | 'warn' | 'danger' | 'info' | 'muted' }> = {
  pending: { label: '承認待ち', tone: 'warn' },
  approved: { label: '承認済み', tone: 'info' },
  executed: { label: '実行済み', tone: 'ok' },
  rejected: { label: '却下', tone: 'muted' },
  expired: { label: '期限切れ', tone: 'muted' },
  failed: { label: '失敗', tone: 'danger' },
};

export function softwareText(spec: {
  family: SoftwareFamily;
  mcVersion: string;
  build?: string;
  loaderVersion?: string;
}): string {
  const extra = spec.build ? ` #${spec.build}` : spec.loaderVersion ? ` (loader ${spec.loaderVersion})` : '';
  return `${softwareLabel[spec.family]} ${spec.mcVersion}${extra}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}
